import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const EnquiryModal = ({ isOpen, onClose, serviceName, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    urgency: '',
    budget: '',
    description: '',
    documents: [],
    agreeTerms: false
  });

  const [errors, setErrors] = useState({});

  const urgencyOptions = [
    { value: 'immediate', label: 'Immediate (Within 24 hours)' },
    { value: 'urgent', label: 'Urgent (Within 1 week)' },
    { value: 'normal', label: 'Normal (Within 2 weeks)' },
    { value: 'flexible', label: 'Flexible (No rush)' }
  ];

  const budgetOptions = [
    { value: 'under-500', label: 'Under $500' },
    { value: '500-1000', label: '$500 - $1,000' },
    { value: '1000-2500', label: '$1,000 - $2,500' },
    { value: '2500-5000', label: '$2,500 - $5,000' },
    { value: 'above-5000', label: 'Above $5,000' },
    { value: 'not-sure', label: 'Not sure yet' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e?.target?.files);
    setFormData(prev => ({ ...prev, documents: files }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone is required';
    if (!formData?.urgency) newErrors.urgency = 'Please select urgency';
    if (!formData?.description?.trim()) newErrors.description = 'Please describe your requirements';
    if (!formData?.agreeTerms) newErrors.agreeTerms = 'You must agree to terms';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">Submit Enquiry</h2>
            <p className="text-sm text-muted-foreground mt-1">For: {serviceName}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="John Doe"
              value={formData?.name}
              onChange={(e) => handleChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />
            <Input
              label="Email Address"
              type="email"
              placeholder="john@example.com"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Phone Number"
              type="tel"
              placeholder="+1 (555) 000-0000"
              value={formData?.phone}
              onChange={(e) => handleChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />
            <Input
              label="Company Name"
              type="text"
              placeholder="Optional"
              value={formData?.company}
              onChange={(e) => handleChange('company', e?.target?.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Urgency Level"
              options={urgencyOptions}
              value={formData?.urgency}
              onChange={(value) => handleChange('urgency', value)}
              error={errors?.urgency}
              required
              placeholder="Select urgency"
            />
            <Select
              label="Budget Range"
              options={budgetOptions}
              value={formData?.budget}
              onChange={(value) => handleChange('budget', value)}
              placeholder="Select budget range"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Describe Your Requirements *
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              placeholder="Please provide detailed information about your service requirements..."
              rows="5"
              className={`w-full px-4 py-3 rounded-lg border ${
                errors?.description ? 'border-error' : 'border-input'
              } bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none`}
            />
            {errors?.description && (
              <p className="text-sm text-error mt-1">{errors?.description}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Upload Documents (Optional)
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Icon name="Upload" size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-foreground mb-1">Click to upload or drag and drop</p>
                <p className="text-xs text-muted-foreground caption">PDF, DOC, DOCX, JPG, PNG (Max 10MB each)</p>
              </label>
            </div>
            {formData?.documents?.length > 0 && (
              <div className="mt-3 space-y-2">
                {formData?.documents?.map((file, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-foreground">
                    <Icon name="FileText" size={16} />
                    <span className="flex-1 truncate">{file?.name}</span>
                    <span className="text-muted-foreground caption">{(file?.size / 1024)?.toFixed(1)} KB</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-muted rounded-lg p-4">
            <Checkbox
              label="I agree to the terms and conditions"
              description="By submitting this enquiry, you agree to be contacted by service providers and OTAssist team regarding your request."
              checked={formData?.agreeTerms}
              onChange={(e) => handleChange('agreeTerms', e?.target?.checked)}
              error={errors?.agreeTerms}
              required
            />
          </div>

          <div className="flex flex-col-reverse md:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="flex-1"
            >
              Submit Enquiry
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnquiryModal;